import React, { useState } from 'react';
import _ from 'lodash';
import copy from 'copy-to-clipboard';
import { toast } from 'react-semantic-toasts';
import { Icon } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import PlaybookTesterLabel from '../../../components/playbook-tester-label';

import {
  approveTestSession,
  removeTestSession,
} from '../approval-unmoderated-video/graphql';

import {
  MARK_PROJECT_AS_DONE,
  GET_TEST_SESSIONS,
} from '../graphql';

import {
  handleMarkProjectAsDoneMutationUpdate,
} from '../approval-moderated-video-link/graphql';

import TesterVideoLink from '../../../../core/components/tester-video-link';
import CapResponsesModal from '../cap-responses-modal';
import ApprovalActionDropdown from '../../../../core/components/approval-action-dropdown';

import {
  ButtonApprove,
  ButtonRemove,
  Button,
  Container,
  ContainerTextName,
  Detail,
  HeaderContainer,
  Loader,
  OtherDetails,
  Tester,
  Undo,
  UndoButton,
  ContainerSpace,
  ContainerParticipants,
} from './styles';

import { TOAST_TIME_IN_MS } from '../constants';
import { CintIdLabel, ContentIdLabels, MrIdLabel } from '../styles';
import RemovedReason from '../removed-reason';
import { registerFunctions } from '../../../../core/hooks/use-function-handler';
import PayDropdown from '../pay-dropdown';
import SegmentParticipant from '../segment-participant';

const ApprovalTreeTestSurvey = (props) => {
  const {
    project, refetch,
  } = props;
  const {
    id: projectId,
    audienceParticipants,
    participantsSource,
    segments,
  } = project;

  const {
    data = {},
    loading,
    errors,
    refetch: refetchTestSessions,
  } = useQuery(GET_TEST_SESSIONS, {
    variables: {
      projectId,
      limit: 1000,
      status: 'processed',
    },
  });

  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [isLoadingRemove, setIsLoadingRemove] = useState(false);
  const [isLoadingTarget, setIsLoadingTarget] = useState('');
  const [showUndo, setShowUndo] = useState(false);
  const [showUndoTarget, setShowUndoTarget] = useState('');

  const handleApproveTestSession = async (id, testerId, approved) => {
    setIsLoadingApprove(true);
    setIsLoadingTarget(testerId);
    setShowUndo(false);
    setShowUndoTarget('');

    try {
      await approveTestSession(id, approved);
      await refetchTestSessions();
      setIsLoadingApprove(false);
      setIsLoadingTarget('');
      refetch();
    } catch (error) {
      setIsLoadingApprove(false);
      setIsLoadingTarget('');
      captureException('HANDLE_APPROVE_TEST_SESSION_TREE_TEST_FAILED', error);
    }
  };

  const handleRemoveTestSession = async (id, testerId, removed) => {
    setIsLoadingRemove(true);
    setIsLoadingTarget(testerId);
    setShowUndo(false);
    setShowUndoTarget('');

    try {
      await removeTestSession(id, removed);
      await refetchTestSessions();
      setIsLoadingRemove(false);
      setIsLoadingTarget('');
      refetch();
    } catch (error) {
      setIsLoadingRemove(false);
      setIsLoadingTarget('');
      captureException('HANDLE_REMOVE_TEST_SESSION_TREE_TEST_FAILED', error);
    }
  };

  const handleMouseEnter = (key) => {
    setShowUndo(true);
    setShowUndoTarget(key);
  };

  const handleMouseLeave = () => {
    setShowUndo(false);
    setShowUndoTarget('');
  };

  const [markProjectAsDone] = useMutation(MARK_PROJECT_AS_DONE, {
    update: (cache, mutation) => handleMarkProjectAsDoneMutationUpdate(cache, mutation, participantsSource),
    onCompleted: refetch,
  });

  const handleMarkProjectAsDone = async () => {
    await markProjectAsDone({
      variables: {
        projectId,
      },
    });
    await refetch();
  };

  const handleCopyCintId = (id) => {
    copy(id);
    toast({
      description: 'Cint id copied to the clipboard!',
      time: TOAST_TIME_IN_MS,
    });
  };

  if (loading) {
    return (
      <Container>
        <Loader size="small" data-loader="approval-tree-test-survey-loader" />
      </Container>
    );
  }

  if (errors) {
    return (
      <ErrorHandler
        error={errors}
        defaultMessage="Error while retrieving the testers."
      />
    );
  }

  const { testSessionsApprovalsTab: testSessions } = data;
  const testSessionsApproved = _.filter(testSessions, { approved: true });
  const testSessionsWithSegment = _.reduce(
    segments,
    (result, segment) => {
      const { name, audienceParticipants: participants } = segment;
      const testSessionsGroupedBySegment = _.filter(testSessions, (testSession) => testSession.tester.segment.name === name);
      return {
        ...result,
        [name]: {
          participants,
          testSessions: testSessionsGroupedBySegment,
        },
      };
    },
    {},
  );

  registerFunctions(`refetch_test_sessions_${projectId}`, {
    func4: refetchTestSessions,
  });

  const disabledActions = isLoadingApprove || isLoadingRemove;

  return (
    <>
      <Container>
        <HeaderContainer>
          <OtherDetails>
            Other Details:
          </OtherDetails>

          <Detail>
            Description:
            {' '}
            {project.description}
          </Detail>

          <Detail>
            Created by:
            {' '}
            {project.createdBy.profile.firstName}
            {' '}
            {project.createdBy.profile.lastName}
          </Detail>

        </HeaderContainer>

        <ContainerSpace>
          <div>
            {segments && segments.map((segment) => (
              <ContainerParticipants>
                <Detail>
                  {`${segment.name} # Participants: ${segment.audienceParticipants}`}
                </Detail>
                <CapResponsesModal
                  label="Response Cap"
                  projectId={projectId}
                  number={segment.audienceParticipants}
                  segmentId={segment.id}
                  refetch={refetch}
                />
              </ContainerParticipants>
            ))}
          </div>
        </ContainerSpace>

        <HeaderContainer>
          <Detail>
            # Surveys received:
            {' '}
            {testSessions.length}
          </Detail>

          <Detail>
            # Surveys approved:
            {' '}
            {testSessionsApproved.length}
          </Detail>
        </HeaderContainer>

        {Object.keys(testSessionsWithSegment).map((key, index) => (
          <>
            <SegmentParticipant
              key={key}
              title={key}
              index={index}
              testSessionsWithSegment={testSessionsWithSegment[key]}
              addApproveAll
              projectId={projectId}
              refetchTestSessions={refetch}
            />

            {testSessionsWithSegment[key].testSessions.length > 0 ? testSessionsWithSegment[key].testSessions.map(({
              id,
              tester,
              approved,
              removed,
            }) => (
              <Tester key={tester.id}>
                <ContainerTextName>
                  <TesterVideoLink
                    tester={tester}
                    project={project}
                    testSessionId={id}
                  />
                </ContainerTextName>

                {!approved && !removed && (
                  <>
                    <ButtonRemove
                      disabled={disabledActions}
                      loading={isLoadingRemove && isLoadingTarget === tester.id}
                      onClick={() => handleRemoveTestSession(id, tester.id, true)}
                    >
                      Remove
                    </ButtonRemove>

                    <ButtonApprove
                      disabled={disabledActions}
                      loading={isLoadingApprove && isLoadingTarget === tester.id}
                      onClick={() => handleApproveTestSession(id, tester.id, true)}
                    >
                      Approve
                    </ButtonApprove>
                  </>
                )}

                {approved && (
                  <UndoButton
                    disabled={disabledActions}
                    key={tester.id}
                    loading={isLoadingApprove && isLoadingTarget === tester.id}
                    onMouseEnter={() => handleMouseEnter(tester.id)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleApproveTestSession(id, tester.id, false)}
                  >
                    <Icon name="check" />
                    {' '}
                    Approved
                    {showUndo && showUndoTarget === tester.id && <Undo>- undo</Undo>}
                  </UndoButton>
                )}

                {removed && (
                  <UndoButton
                    disabled={disabledActions}
                    key={tester.id}
                    loading={isLoadingRemove && isLoadingTarget === tester.id}
                    onMouseEnter={() => handleMouseEnter(tester.id)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleRemoveTestSession(id, tester.id, false)}
                  >
                    <Icon name="check" />
                    {' '}
                    Removed
                    {showUndo && showUndoTarget === tester.id && <Undo>- undo</Undo>}
                  </UndoButton>
                )}

                <ApprovalActionDropdown
                  tester={tester}
                  projectId={project.id}
                />

                {tester.user.tester && !tester.user.guest && project.participantsSource === 'playbook' && (
                  <PlaybookTesterLabel />
                )}

                {!!tester.userInterviewsParticipantId && (removed || approved) && (
                  <PayDropdown
                    testerId={tester.id}
                    userInterviewsPayStatus={tester.userInterviewsPayStatus}
                    userInterviewsStatus={tester.userInterviewsStatus}
                    refetchParticipants={refetch}
                  />
                )}

                <ContentIdLabels>
                  {tester.cintId && (
                    <CintIdLabel onClick={() => handleCopyCintId(tester.cintId)}>
                      {tester.cintId}
                    </CintIdLabel>
                  )}

                  {tester.mrId && (
                    <MrIdLabel>
                      {tester.mrId}
                    </MrIdLabel>
                  )}
                </ContentIdLabels>

                {removed && (
                  <RemovedReason
                    testSessionId={id}
                    testerRemovedReason={tester?.removedReason}
                    refetch={refetch}
                  />
                )}
              </Tester>
            )) : (
              <Tester>
                No sessions
              </Tester>
            )}
          </>
        ))}

        {(testSessionsApproved.length >= audienceParticipants) && (
          <Button onClick={handleMarkProjectAsDone}>
            Mark as Done
          </Button>
        )}

      </Container>
    </>
  );
};

ApprovalTreeTestSurvey.propTypes = {
  project: PropTypes.shape().isRequired,
  refetch: PropTypes.func.isRequired,
};

export default ApprovalTreeTestSurvey;
